import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { forwardRef, InputHTMLAttributes } from 'react'

/**
 * Máscara de moeda pt-BR: os dígitos digitados entram como centavos
 * ("5" → "0,05", "1500" → "15,00", "123456" → "1.234,56").
 * O valor continua sendo texto compatível com realToCents().
 */
export const maskMoney = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').replace(/^0+/, '').slice(0, 13)
    if (!digits) return ''
    return (Number(digits) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Percentual com até 2 casas, vírgula decimal e teto de 100. */
export const maskPercent = (raw: string, previous = ''): string => {
    let value = raw.replace(/\./g, ',').replace(/[^\d,]/g, '')
    const [int = '', ...rest] = value.split(',')
    const hasComma = value.includes(',')
    value = hasComma ? `${int},${rest.join('').slice(0, 2)}` : int
    value = value.replace(/^0+(?=\d)/, '')
    const numeric = Number(value.replace(',', '.'))
    if (Number.isFinite(numeric) && numeric > 100) return previous
    return value
}

type BaseProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
    value?: string
    onChange: (value: string) => void
    invalid?: boolean
}

const Affix = ({ children, side }: { children: string; side: 'left' | 'right' }) => (
    <span className={cn('pointer-events-none absolute top-1/2 -translate-y-1/2 text-sm text-muted-foreground', side === 'left' ? 'left-3' : 'right-3')}>
        {children}
    </span>
)

export const MoneyInput = forwardRef<HTMLInputElement, BaseProps>(({ value = '', onChange, invalid, className, ...props }, ref) => (
    <div className="relative">
        <Affix side="left">R$</Affix>
        <Input
            ref={ref}
            inputMode="numeric"
            autoComplete="off"
            placeholder="0,00"
            aria-invalid={invalid || undefined}
            className={cn('pl-9 text-right tabular-nums', invalid && 'border-destructive', className)}
            value={value}
            onChange={(event) => onChange(maskMoney(event.target.value))}
            {...props}
        />
    </div>
))
MoneyInput.displayName = 'MoneyInput'

export const PercentInput = forwardRef<HTMLInputElement, BaseProps>(({ value = '', onChange, invalid, className, ...props }, ref) => (
    <div className="relative">
        <Input
            ref={ref}
            inputMode="decimal"
            autoComplete="off"
            placeholder="0"
            aria-invalid={invalid || undefined}
            className={cn('pr-8 text-right tabular-nums', invalid && 'border-destructive', className)}
            value={value}
            onChange={(event) => onChange(maskPercent(event.target.value, value))}
            {...props}
        />
        <Affix side="right">%</Affix>
    </div>
))
PercentInput.displayName = 'PercentInput'
